import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart, Pie, Cell, Tooltip } from 'recharts';
import { Badge } from '@/components/ui/badge';
import { X, Wifi } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Device, Position } from '@/lib/traccar-api';

export type DeviceStatusFilter = 'moving' | 'idle' | 'parked' | 'offline' | null;

interface DeviceStatusChartProps {
  devices: Device[];
  positions: Position[];
  activeFilter?: DeviceStatusFilter;
  onFilterChange?: (filter: DeviceStatusFilter) => void;
}

export const DeviceStatusChart = ({
  devices,
  positions,
  activeFilter,
  onFilterChange
}: DeviceStatusChartProps) => {
  const { t } = useTranslation();

  const positionMap = new Map(positions.map(p => [p.deviceId, p]));

  const movingCount = devices.filter(d => {
    if (d.status !== 'online') return false;
    const pos = positionMap.get(d.id);
    return pos && pos.speed > 0;
  }).length;

  const idleCount = devices.filter(d => {
    if (d.status !== 'online') return false;
    const pos = positionMap.get(d.id);
    if (!pos || pos.speed > 0) return false;
    return !!(pos.attributes?.ignition);
  }).length;

  const parkedCount = devices.filter(d => {
    if (d.status !== 'online') return false;
    const pos = positionMap.get(d.id);
    if (!pos || pos.speed > 0) return false;
    return !pos.attributes?.ignition;
  }).length;

  const offlineCount = devices.filter(d => d.status !== 'online').length;
  const onlineCount = movingCount + idleCount + parkedCount;
  const onlinePercent = devices.length > 0 ? Math.round((onlineCount / devices.length) * 100) : 0;

  const data = [
    { name: t('map.moving'), value: movingCount, color: '#22c55e', filter: 'moving' as const },
    { name: t('map.idle'),    value: idleCount,   color: '#eab308', filter: 'idle' as const },
    { name: t('map.parked'),  value: parkedCount, color: '#f97316', filter: 'parked' as const },
    { name: t('map.offline'), value: offlineCount, color: '#ef4444', filter: 'offline' as const },
  ].filter(d => d.value > 0);

  const handlePieClick = (entry: typeof data[0]) => {
    if (onFilterChange) {
      onFilterChange(activeFilter === entry.filter ? null : entry.filter);
    }
  };

  const activeCount =
    activeFilter === 'moving' ? movingCount :
    activeFilter === 'idle'   ? idleCount :
    activeFilter === 'parked' ? parkedCount :
    activeFilter === 'offline' ? offlineCount :
    onlineCount;

  return (
    <Card className="h-full flex flex-col overflow-hidden">
      <CardHeader className="py-2 pl-4 pr-14 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-success/10">
              <Wifi className="h-3.5 w-3.5 text-success" />
            </div>
            <div>
              <CardTitle className="text-sm">{t('dashboard.deviceStatus')}
              <span className="block truncate text-xs font-normal text-muted-foreground">{devices.length} {t('devices.title').toLowerCase()} · {onlinePercent}% online</span>
              </CardTitle>
            </div>
          </div>
          {activeFilter && (
            <Badge
              variant="secondary"
              className="gap-1 cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-colors text-xs"
              onClick={() => onFilterChange?.(null)}
            >
              {activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)}
              <X className="h-3 w-3" />
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="py-1 px-4 flex-1 min-h-0">
        <div className="flex items-center gap-3 h-full">
          <div className="relative flex-shrink-0">
            <PieChart width={100} height={100}>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={30}
                outerRadius={45}
                paddingAngle={2}
                dataKey="value"
                style={{ cursor: 'pointer' }}
                onClick={(_, index) => handlePieClick(data[index])}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    opacity={activeFilter && activeFilter !== entry.filter ? 0.3 : 1}
                    stroke={activeFilter === entry.filter ? 'hsl(var(--foreground))' : 'none'}
                    strokeWidth={activeFilter === entry.filter ? 2 : 0}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
              />
            </PieChart>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <span className="text-lg font-bold">{activeCount}</span>
                <span className="text-[10px] text-muted-foreground block">
                  {activeFilter ?? 'online'}
                </span>
              </div>
            </div>
          </div>
          <div className="flex-1 space-y-1 overflow-hidden">
            {data.map((item) => {
              const percentage = devices.length > 0 ? Math.round((item.value / devices.length) * 100) : 0;
              return (
                <button
                  key={item.filter}
                  onClick={() => handlePieClick(item)}
                  className="w-full flex items-center gap-2 text-xs py-0.5 px-1 rounded hover:bg-muted/50 transition-colors"
                  style={{ opacity: activeFilter && activeFilter !== item.filter ? 0.4 : 1 }}
                >
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                  <span className="text-muted-foreground truncate flex-1 text-left">{item.name}</span>
                  <span className="font-medium">{item.value} ({percentage}%)</span>
                </button>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
